import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, put } from '@/lib/api'

// ==================== Types ====================

export type NotificationSettingsType = 
  | 'approval_request'
  | 'approval_result'
  | 'task_assignment'
  | 'task_mention'
  | 'system_announcement'

export type NotificationSettingsChannel = 'email' | 'inApp' | 'slack'

export interface ChannelSettings {
  enabled: boolean
}

export interface TriggerSettings {
  enabled: boolean
}

export interface SystemNotificationSettings {
  channels: {
    email: ChannelSettings
    inApp: ChannelSettings
    slack: ChannelSettings
  }
  triggers: {
    [key in NotificationSettingsType]: TriggerSettings
  }
}

export interface UserNotificationPreferences {
  [key: string]: {
    email: boolean
    inApp: boolean
    slack: boolean
  }
}

export interface UpdateSystemNotificationInput {
  channels?: {
    email?: ChannelSettings
    inApp?: ChannelSettings
  }
  triggers?: {
    [key in NotificationSettingsType]?: TriggerSettings
  }
}

export interface UpdateUserNotificationInput {
  [key: string]: {
    email: boolean
    inApp: boolean
    slack: boolean
  }
}

// ==================== Query Keys ====================

export const notificationSettingsKeys = {
  all: ['notification-settings'] as const,
  system: () => [...notificationSettingsKeys.all, 'system'] as const,
  user: () => [...notificationSettingsKeys.all, 'user'] as const,
}

// ==================== System Notification Hooks ====================

/**
 * 获取系统通知设置
 */
export function useSystemNotifications() {
  return useQuery({
    queryKey: notificationSettingsKeys.system(),
    queryFn: () => get<SystemNotificationSettings>('/system/notifications'),
  })
}

/**
 * 更新系统通知设置
 */
export function useUpdateSystemNotifications() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateSystemNotificationInput) =>
      put<SystemNotificationSettings>('/system/notifications', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationSettingsKeys.system() })
    },
  })
}

// ==================== User Notification Hooks ====================

/**
 * 获取用户通知偏好
 */
export function useUserNotifications() {
  return useQuery({
    queryKey: notificationSettingsKeys.user(),
    queryFn: () => get<UserNotificationPreferences>('/users/me/notifications'),
  })
}

/**
 * 更新用户通知偏好
 */
export function useUpdateUserNotifications() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateUserNotificationInput) =>
      put<UserNotificationPreferences>('/users/me/notifications', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationSettingsKeys.user() })
    },
  })
}

// ==================== Helper Constants ====================

export const NOTIFICATION_TYPE_LABELS: Record<NotificationSettingsType, string> = {
  approval_request: '审批请求',
  approval_result: '审批结果',
  task_assignment: '任务分配',
  task_mention: '任务提及',
  system_announcement: '系统公告',
}

export const NOTIFICATION_CHANNEL_LABELS: Record<NotificationSettingsChannel, string> = {
  email: '邮件通知',
  inApp: '站内通知',
  slack: 'Slack 通知',
}
